import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import cx from 'clsx';
import { useHistory } from 'react-router-dom';
import { unionBy } from 'lodash';
import produce from 'immer';
import moment, { Moment } from 'moment';
import DatePicker from 'antd/es/date-picker';
import Switch from 'antd/es/switch';
import message from 'antd/es/message';

import { IconRcDropdown } from '@/assets';
import { IconAddressAvatar } from '@/assets/icon/avatar';
import { IconCloseFilter, IconDatePicker, IconDeposit } from '@/assets/history';
import { Card, Dropdown, ChainPopover, Loading, Button, Modal } from '@/components';
import {
  usePaidFeatureAvailability,
  PAID_FEATURE,
  useSwitch,
  useUpdateChainQueryParam,
  usePageChain,
  useUserUsedChains,
} from '@/hooks';
import {
  wrapUrlInImgOrDefault,
  formatNum,
  CHAINS_DICT,
  useCurrentUserId,
  login,
  devLog,
} from '@/utils';

import { analysisHistoryList, exportHistoryList } from '../../utils';
import { useAllHistoryList } from '../../hooks';
import { TopItem, TokenHistoryItem } from '../../types';
import { AnalysisHistoryList } from '../List';
import { LimitTxTypeMenus, LimitTxAmountMenus } from './const';
import style from './index.module.css';

moment().utc(true);

type TopItemSelected = TopItem;

const EmptyTopPlaceholder = () => {
  const { t } = useTranslation();

  return <div className={style.emptyTopPlaceholder}>{t('HistoryAnalysisView.1vwh8ub')}</div>;
};

const TopItemComponent = ({
  id,
  value,
  icon,
  name,
  type,
  chain,
  onClick,
  selected,
  isOpacity,
  showAmount,
  isDeposit,
}: TopItem & {
  onClick: (item: TopItemSelected) => void;
  selected: boolean;
  isOpacity: boolean;
  showAmount: boolean;
}) => {
  const { t } = useTranslation();
  const handleClick = useCallback(
    () =>
      onClick({
        id,
        value,
        icon,
        name,
        type,
        chain,
      }),
    [chain, icon, id, name, onClick, type, value],
  );
  const showDeposit = useMemo(() => type === 'cex' && isDeposit, [isDeposit, type]);
  return (
    <div
      className={cx(
        style.topItemComponent,
        selected && style.selected,
        isOpacity && style.isOpacity,
      )}
      key={id}
      onClick={handleClick}>
      <div className={style.topItemIconWrapper}>
        {wrapUrlInImgOrDefault(
          icon,
          20,
          {
            borderRadius: '50%',
          },
          type === 'token' ? undefined : IconAddressAvatar,
        )}
        {chain && CHAINS_DICT[chain]?.logo
          ? wrapUrlInImgOrDefault(CHAINS_DICT[chain].logo, 12, {
              position: 'absolute',
              right: '-4px',
              top: '-4px',
            })
          : null}
        {showDeposit ? <IconDeposit className={style.depositIcon} /> : null}
      </div>
      <span className={style.name}>{name || t('HistoryAnalysisView.unknown')}</span>
      {(showAmount || selected) && value >= 1 ? (
        <span className={style.value}>{formatNum(value, 0, { prefix: '$', abbrStart: 1 })}</span>
      ) : null}
    </div>
  );
};

const TopItemSelector = ({
  options = [],
  onChange,
  informationOptions,
  disabledCb,
  selectedList,
}: {
  options?: TopItem[];
  disabledCb: null | (() => void);
  onChange: (selected: TopItemSelected[]) => void;
  informationOptions?: TopItem[];
  selectedList: TopItemSelected[];
}) => {
  const [selected, setSelected] = useState<TopItemSelected[]>([]);
  const selectedIds = useMemo(() => selected.map(item => item.id), [selected]);
  const displayList = useMemo(
    () =>
      selected
        .concat(options.filter(item => !selectedIds.includes(item.id)))
        .filter(Boolean) as TopItem[],
    [options, selected, selectedIds],
  );

  useEffect(() => {
    setSelected(list => {
      return produce(list, draft => {
        selected.forEach((selectedItem, i) => {
          const target = informationOptions?.find(
            item => item.id === selectedItem.id && item.type === selectedItem.type,
          );
          if (draft[i]) {
            if (target) {
              draft[i] = target;
            } else {
              draft[i].value = 0;
            }
          }
        });
      });
    });
  }, [informationOptions, selected]);

  const handleClickOption = useCallback(
    (option: TopItemSelected) => {
      if (disabledCb) {
        disabledCb();
        return;
      }
      setSelected(list => {
        let result = [];
        if (list.find(item => item?.id === option.id)) {
          result = list.filter(item => item.id !== option.id);
        } else {
          result = [...list, option];
        }
        onChange(result);

        return result;
      });
    },
    [disabledCb, onChange],
  );

  useEffect(() => {
    setSelected(selectedList);
  }, [selectedList]);

  // useEffect(() => {
  //   onChange(selected);
  // }, [onChange, selected]);

  return (
    <div className={style.topItemsList}>
      {displayList.map(item => (
        <TopItemComponent
          {...item}
          key={item.id}
          selected={selectedIds.includes(item.id)}
          isOpacity={Boolean(selectedIds.length && !selectedIds.includes(item.id))}
          showAmount={!selectedIds.length}
          onClick={handleClickOption}
        />
      ))}
    </div>
  );
};

const FilterItem = ({
  item,
  onDelete,
}: {
  item: TopItemSelected;
  onDelete: (item: TopItemSelected) => void;
}) => {
  const handleClick = useCallback(() => {
    onDelete(item);
  }, [item, onDelete]);
  return (
    <div className={style.filterItem}>
      {item.name}
      <div className={style.icon} onClick={handleClick}>
        <IconCloseFilter />
      </div>
    </div>
  );
};

type HistoryAnalysisViewProps = {
  address: string;
  goBack?: () => void;
  timeRange?: number[];
  className?: string;
};

export const HistoryAnalysisView = ({
  address,
  timeRange,
  className,
  goBack,
}: HistoryAnalysisViewProps) => {
  const { t } = useTranslation();

  const { data: allHistoryList, isLoading, isError } = useAllHistoryList(address, timeRange);
  const currentUserId = useCurrentUserId();

  const [orConditionList, setOrConditionList] = useState<((record: TokenHistoryItem) => boolean)[]>(
    [],
  );
  const { data: enablePaidFeature } = usePaidFeatureAvailability(PAID_FEATURE.HISTORY_ANALYSIS);

  const history = useHistory();

  const { data: usedChains } = useUserUsedChains(address);
  const [selectedCategoryList, setSelectedCategoryList] = useState<TopItemSelected[]>([]);
  const [selectedTokenList, setSelectedTokenList] = useState<TopItemSelected[]>([]);
  const [, setPageChain] = usePageChain();

  const showPaidFeatureModal = useCallback(() => {
    if (!currentUserId) {
      login();
      return;
    }
    Modal.info({
      title: t('HistoryAnalysisView.ohe5ed'),
      content: t('HistoryAnalysisView.paidContent'),
      confirmBtnText: t('AvatarEditable_AvatarModal.1c37jyp'),
      contentClassName: style.exportInfoContent,
      onConfirmClick: () => {
        history.push('/paid');
      },
    }).catch(e => {
      console.log('error', e);
    });
  }, [currentUserId, history, t]);

  const handlerOperationWithChecking = useCallback(
    handler => {
      return (...params: any[]) => {
        if (!enablePaidFeature) {
          showPaidFeatureModal();
          return;
        }

        handler(...params);
      };
    },
    [enablePaidFeature, showPaidFeatureModal],
  );

  // and condition list
  const { on: hideScamHistory, toggle: toggleScamHistory } = useSwitch(true);
  const [chain, setChain] = useUpdateChainQueryParam();

  const [limitTxAmount, setLimitTxAmount] = useState<number | undefined>(undefined);
  const [limitTxType, setLimitTxType] = useState<'nft' | 'token' | undefined>(undefined);

  // or condition list
  const [filterTokenUuids, setFilterTokenUuids] = useState<string[]>([]);
  const [limitStartDate, setLimitStartDate] = useState<Moment | null>(
    timeRange ? moment.unix(timeRange[0]) : null,
  );
  const [limitEndDate, setLimitEndDate] = useState<Moment | null>(
    timeRange ? moment.unix(timeRange[1]) : null,
  );

  useEffect(() => {
    if (timeRange) {
      setLimitStartDate(moment.unix(timeRange[0]));
      setLimitEndDate(moment.unix(timeRange[1]));
    }
  }, [timeRange]);

  const andConditionList = useMemo(() => {
    const conditionList: ((record: TokenHistoryItem) => boolean)[] = [];
    if (enablePaidFeature && limitStartDate) {
      const laterThan = limitStartDate.startOf('day').unix();
      conditionList.push(record => record.time_at >= laterThan);
    }
    if (enablePaidFeature && limitEndDate) {
      const earlierThan = limitEndDate.endOf('day').unix();
      conditionList.push(record => record.time_at <= earlierThan);
    }

    if (hideScamHistory) {
      conditionList.push(record => !record.is_scam);
    }
    if (enablePaidFeature && chain) {
      conditionList.push(record => record.chain === chain);
    }
    return conditionList;
  }, [chain, enablePaidFeature, hideScamHistory, limitEndDate, limitStartDate]);

  const runtimeAndConditionList = useMemo(() => {
    const runtimeConditionList: ((
      record: TokenHistoryItem & { transactionAmount?: number; hasNft?: boolean },
    ) => boolean)[] = [];

    if (limitTxAmount) {
      runtimeConditionList.push(record => (record.transactionAmount || 0) > limitTxAmount);
    }
    if (limitTxType) {
      runtimeConditionList.push(
        limitTxType === 'nft' ? record => Boolean(record.hasNft) : record => !record.hasNft,
      );
    }
    return runtimeConditionList;
  }, [limitTxAmount, limitTxType]);

  useEffect(() => {
    const list = allHistoryList?.history_list;
    if (!list?.length || (limitStartDate && limitEndDate)) {
      return;
    }

    setLimitStartDate(moment.unix(list[list?.length - 1].time_at).startOf('day'));
    setLimitEndDate(moment.unix(list[0].time_at).endOf('day'));
  }, [allHistoryList?.history_list, limitStartDate, limitEndDate]);

  const handleClearDate = useCallback(() => {
    setLimitStartDate(null);
    setLimitEndDate(null);
  }, []);

  const analysisResult = useMemo(
    () =>
      allHistoryList &&
      analysisHistoryList(
        allHistoryList,
        orConditionList,
        andConditionList,
        runtimeAndConditionList,
        filterTokenUuids,
      ),
    [allHistoryList, andConditionList, filterTokenUuids, orConditionList, runtimeAndConditionList],
  );

  useEffect(() => {
    setOrConditionList(
      selectedCategoryList.map(item => {
        if (item.type === 'cex') {
          return record => record.cex_id === item.id;
        } else if (item.type === 'project') {
          return record => record.project_id === item.id;
        } else if (item.type === 'addr') {
          return record => record.other_addr === item.id;
        } else {
          return () => true;
        }
      }),
    );
  }, [selectedCategoryList]);

  useEffect(() => {
    setFilterTokenUuids(selectedTokenList.map(item => item.id));
  }, [selectedTokenList]);

  const limitTxTypeMenus = useMemo(
    () =>
      LimitTxTypeMenus.map(item => ({
        ...item,
        onClick: handlerOperationWithChecking(() => {
          setLimitTxType(item.value);
        }),
      })),
    [handlerOperationWithChecking],
  );
  const limitTxAmountMenus = useMemo(
    () =>
      LimitTxAmountMenus.map(item => ({
        ...item,
        onClick: handlerOperationWithChecking(() => {
          setLimitTxAmount(item.value);
        }),
      })),
    [handlerOperationWithChecking],
  );

  const setGlobalChain = useCallback(
    (chain: string) => {
      setChain(chain);
      setPageChain(chain);
    },
    [setChain, setPageChain],
  );

  const exportHistory = useCallback(() => {
    exportHistoryList({
      transactions: analysisResult?.filterHistoryList,
      token_dict: analysisResult?.token_dict,
      protocol_dict: analysisResult?.project_dict,
      address,
    });
  }, [
    address,
    analysisResult?.filterHistoryList,
    analysisResult?.project_dict,
    analysisResult?.token_dict,
  ]);

  const disabledSelectDate = useCallback(
    (data: moment.Moment) => {
      return (
        Boolean(
          allHistoryList?.history_list[allHistoryList?.history_list.length - 1] &&
            moment
              .unix(allHistoryList?.history_list[allHistoryList?.history_list.length - 1].time_at)
              .subtract(2, 'day')
              .isAfter(data),
        ) ||
        Boolean(
          allHistoryList?.history_list[allHistoryList?.history_list.length - 1] &&
            moment.unix(allHistoryList?.history_list[0].time_at).add(1, 'day').isBefore(data),
        )
      );
    },
    [allHistoryList?.history_list],
  );

  // 只选择了 地址， 那么token 列表 展示与地址关联的 token
  // 只选择了token,  那么地址列表展示与该token 关联的地址
  // 同时选了一个地址 和 一个token ,  那么置灰的可选列表 就变成了 [关联的地址 + top地址]、 [关联的token + top token]
  const displayTopCategoryList = useMemo(() => {
    if (!selectedCategoryList.length && selectedTokenList.length) {
      return analysisResult?.filteredTopCategoryList.slice(0, 20);
    }
    return unionBy(
      analysisResult?.filteredTopCategoryList.slice(0, 20).concat(analysisResult?.topCategoryList),
      item => `${item.id}_${item.type}`,
    );
  }, [
    analysisResult?.filteredTopCategoryList,
    analysisResult?.topCategoryList,
    selectedCategoryList.length,
    selectedTokenList.length,
  ]);

  const displayTopTokenList = useMemo(() => {
    if (!selectedTokenList.length && selectedCategoryList.length) {
      return analysisResult?.filteredTopTokenList.slice(0, 20);
    }
    return unionBy(
      analysisResult?.filteredTopTokenList.slice(0, 20).concat(analysisResult?.topTokenList),
      item => `${item.id}_${item.type}`,
    );
  }, [
    analysisResult?.filteredTopTokenList,
    analysisResult?.topTokenList,
    selectedCategoryList.length,
    selectedTokenList.length,
  ]);

  const hitTxLimit = useMemo(
    () => (allHistoryList?.history_list?.length || 0) >= 10000,
    [allHistoryList?.history_list?.length],
  );

  const handleTokenClick = useCallback(
    (data: { uuid: string }) => {
      const { uuid } = data;
      devLog('click history token', data, analysisResult?.filteredTopTokenList);
      const clickTokenList = analysisResult?.filteredTopTokenList?.filter(
        token => token.id === uuid,
        // || token.name === name, // 筛选名字相同的代币，暂时不需要
      );

      if (clickTokenList?.length) {
        const list = unionBy(
          [...selectedTokenList, ...clickTokenList],
          item => `${item.id}_${item.type}`,
        );
        setSelectedTokenList(list);
      } else {
        message.warn(t('HistoryAnalysisView.tokenWarn'));
      }
    },
    [analysisResult?.filteredTopTokenList, selectedTokenList, t],
  );

  if (isLoading) {
    return (
      <Card className={cx(style.loadingPage, className)}>
        <Loading size={38} />
        <span>{t('HistoryAnalysisView.19uz6eb')}</span>
      </Card>
    );
  }

  return (
    <div className={cx(style.historyAnalysisView, className)}>
      {isError ? null : (
        <div className={style.toolbar}>
          <div className={cx(style.datePickerGroup, timeRange && style.isDisabled)}>
            <DatePicker
              className={style.datePicker}
              format="YYYY-MM-DD"
              value={limitStartDate}
              disabledDate={disabledSelectDate as any}
              placeholder={t('HistoryAnalysisView.from-date')}
              showToday={false}
              allowClear={false}
              onChange={handlerOperationWithChecking(setLimitStartDate)}
              disabled={!!timeRange}
            />
            ~
            <DatePicker
              className={style.datePicker}
              format="YYYY-MM-DD"
              disabledDate={disabledSelectDate as any}
              value={limitEndDate}
              placeholder={t('HistoryAnalysisView.to-date')}
              allowClear={false}
              showToday={false}
              onChange={handlerOperationWithChecking(setLimitEndDate)}
              disabled={!!timeRange}
            />
            <div className={style.closeButton}>
              <IconDatePicker className={cx(style.invisibleHover)} />
              <IconCloseFilter
                className={cx(style.icon)}
                onClick={handlerOperationWithChecking(handleClearDate)}
              />
            </div>
          </div>

          <ChainPopover
            value={chain}
            onChange={handlerOperationWithChecking(setGlobalChain)}
            enabledChains={usedChains?.chains}
            placement={'bottomLeft'}
            showAllSupportedChain={false}
            lableClassName={style.dropdownBox}
          />

          <Dropdown
            overlayClassName={style.selectDropdown}
            getPopupContainer={dom => dom.parentElement!}
            inParent
            data={limitTxTypeMenus}
            trigger={['click']}
            placement={'bottomRight'}>
            <div className={style.dropdownBox}>
              {limitTxTypeMenus.find(item => item.value === limitTxType)?.label}
              <IconRcDropdown className={'analysis-dropdown-icon'} />
            </div>
          </Dropdown>
          <Dropdown
            overlayClassName={style.selectDropdown}
            getPopupContainer={dom => dom.parentElement!}
            inParent
            data={limitTxAmountMenus}
            trigger={['click']}
            placement={'bottomRight'}>
            <div className={style.dropdownBox}>
              {limitTxAmountMenus.find(item => item.value === limitTxAmount)?.label}
              <IconRcDropdown className={'analysis-dropdown-icon'} />
            </div>
          </Dropdown>

          <div className={style.hideScam}>
            {t('HistoryAnalysisView.1aapjsq')}
            <Switch
              className={style.hideScamSwitch}
              defaultChecked={hideScamHistory}
              checked={hideScamHistory}
              onChange={handlerOperationWithChecking(toggleScamHistory)}
            />
          </div>
        </div>
      )}
      {selectedCategoryList.length || selectedTokenList.length ? (
        <div className={style.filterList}>
          <div className={style.filterTitle}>{t('HistoryAnalysisView.ikdhdh')}</div>
          <div className={style.filterItems}>
            {selectedCategoryList.map(item => (
              <FilterItem
                item={item}
                onDelete={handlerOperationWithChecking(() =>
                  setSelectedCategoryList(list => list.filter(x => x.id !== item.id)),
                )}
              />
            ))}
            {selectedCategoryList.length && selectedTokenList.length ? (
              <div className={style.splitLine} />
            ) : null}
            {selectedTokenList.map(item => (
              <FilterItem
                item={item}
                onDelete={handlerOperationWithChecking(() =>
                  setSelectedTokenList(list => list.filter(x => x.id !== item.id)),
                )}
              />
            ))}
          </div>
        </div>
      ) : null}

      <Card className={style.detailsPicker}>
        <div>
          <div className={style.title}>{t('HistoryAnalysisView.9ugtja')}</div>
          {displayTopCategoryList?.length ? (
            <TopItemSelector
              options={displayTopCategoryList}
              disabledCb={enablePaidFeature ? null : showPaidFeatureModal}
              onChange={setSelectedCategoryList}
              informationOptions={analysisResult?.filteredTopCategoryList}
              selectedList={selectedCategoryList}
            />
          ) : (
            <EmptyTopPlaceholder />
          )}
        </div>
        <div>
          <div className={style.title}>{t('HistoryAnalysisView.1rmaj3p')}</div>
          {displayTopTokenList?.length ? (
            <TopItemSelector
              options={displayTopTokenList}
              disabledCb={enablePaidFeature ? null : showPaidFeatureModal}
              onChange={setSelectedTokenList}
              informationOptions={analysisResult?.filteredTopTokenList}
              selectedList={selectedTokenList}
            />
          ) : (
            <EmptyTopPlaceholder />
          )}
        </div>
      </Card>

      <div className={style.historyListHeader}>
        <span className={style.totalTx}>
          <span className={style.totalTxAmount}>
            {analysisResult?.filterHistoryList.length || 0}
          </span>{' '}
          {t('HistoryAnalysisView.wbfm8l')}
          {hitTxLimit ? (
            <span className={style.limitTxTips}>{t('HistoryAnalysisView.gdsuas')}</span>
          ) : null}
        </span>
        {analysisResult?.filterHistoryList.length ? (
          <Button
            type="default"
            onClick={handlerOperationWithChecking(exportHistory)}
            className={style.downloadButton}>
            {t('HistoryAnalysisView.1hprots')}
          </Button>
        ) : null}
      </div>
      <AnalysisHistoryList
        className={style.historyList}
        historyPage={analysisResult?.filterHistoryList}
        dictInfo={allHistoryList}
        isLoading={isLoading}
        handleTokenClick={handlerOperationWithChecking(handleTokenClick)}
      />
    </div>
  );
};
